import React from "react";

/**
 * Global error boundary. Any uncaught render/runtime error in the tree below is
 * caught here and shown as a branded, recoverable screen instead of a blank
 * black page. Also records the error to window.dataLayer (if present) so real
 * failures can be diagnosed from analytics without exposing details to users.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Unexpected error" };
  }

  componentDidCatch(error, info) {
    // Never throw from here. Log for diagnosis only.
    try {
      // eslint-disable-next-line no-console
      console.error("App crash caught by ErrorBoundary:", error, info);
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "ascendia_app_error",
          message: (error && error.message) || "unknown",
          stack: (error && error.stack ? String(error.stack).slice(0, 500) : ""),
          path: window.location ? window.location.pathname : "",
          ts: Date.now(),
        });
      }
    } catch (e) { /* swallow */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (e) { /* noop */ }
  };

  handleHome = () => {
    try { window.location.assign("/"); } catch (e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-[#0B0B0C] text-white flex items-center justify-center px-4"
      >
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141416] p-8 text-center shadow-2xl">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="#DFC878" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="font-display text-xl font-semibold">Something interrupted this screen</h1>
          <p className="mt-2 text-sm text-white/60">
            Sorry about that — your details are safe. Please reload to continue where you left off.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={this.handleReload}
              data-testid="app-error-reload"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#C8A14A] px-5 py-2.5 text-sm font-semibold text-black hover:brightness-95 transition"
            >
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm text-white hover:bg-white/5 transition"
            >
              Back to home
            </button>
          </div>
          <a
            href="https://wa.me/972587790553"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block text-xs text-[#DFC878] hover:underline"
          >
            Need help now? Message us on WhatsApp
          </a>
        </div>
      </div>
    );
  }
}
